import { useState, useMemo, useEffect } from 'react';
import FilterChip from '../Home/SearchFilter/FilterChip';
import TeamModal from '../Home/SearchFilter/modals/TeamModal';
import { FILTER_COLORS } from '../Home/SearchFilter/types';
import { useGetCurrentUserQuery, useGetOfficesQuery, useGetGroupsQuery } from 'utils/wemble-api';
import { TeamFacetCount } from 'utils/wemble-api.generated';

interface TeamSelectProps {
  teams?: TeamFacetCount[]; // If provided (from search facets), use these with counts
  value: string[];
  onChange: (teamIds: string[]) => void;
  restrictToUserGroups?: boolean;
  variant?: 'chip' | 'select'; // chip = blue on grey (for filter bar), select = white standalone
  externalOpen?: boolean; // Allow parent to trigger modal open
  onExternalClose?: () => void; // Notify parent when modal closes
}

const TeamSelect = ({ teams: teamsProp, value, onChange, restrictToUserGroups = false, variant = 'select', externalOpen, onExternalClose }: TeamSelectProps) => {
  const { data: offices } = useGetOfficesQuery();
  const { data: groupsRes } = useGetGroupsQuery();

  // Build teams from offices + groups if not provided via facets
  const teams: TeamFacetCount[] = useMemo(() => {
    if (teamsProp) return teamsProp;
    if (!offices || !groupsRes?.groups) return [];
    return offices.map(o => {
      const groupId = typeof o.group === 'string' ? o.group : o.group?._id;
      const group = groupsRes.groups?.find(g => g._id === groupId);
      if (!groupId || !group) return null;
      return {
        _id: o._id!,
        name: o.name || '',
        groupId,
        groupName: group.name || '',
        count: 0,
      };
    }).filter(Boolean) as TeamFacetCount[];
  }, [teamsProp, offices, groupsRes]);
  const [modalOpen, setModalOpen] = useState(false);
  const [openClean, setOpenClean] = useState(false);
  const { data: currentUser } = useGetCurrentUserQuery();

  // Allow parent to trigger modal open
  useEffect(() => {
    if (externalOpen) setModalOpen(true);
  }, [externalOpen]);

  // Filter teams if restricted to user's groups
  const availableTeams = (() => {
    if (!restrictToUserGroups || !currentUser) return teams;
    const userGroupIds = [
      typeof currentUser.group === 'string' ? currentUser.group : currentUser.group?._id,
      typeof currentUser.secondaryGroup === 'string' ? currentUser.secondaryGroup : (currentUser.secondaryGroup as any)?._id,
    ].filter(Boolean);
    return teams.filter(t => userGroupIds.includes(t.groupId));
  })();

  const isAll = value.length === 0 || (value.length === 1 && value[0] === '__all__');

  const displayTeams = restrictToUserGroups ? availableTeams : teams;

  // Build chip label
  const label = (() => {
    if (isAll) {
      if (restrictToUserGroups && displayTeams.length > 0) {
        const groupNames = Array.from(new Set(displayTeams.map(t => t.groupName)));
        if (groupNames.length === 1) return groupNames[0];
        if (groupNames.length === 2) return groupNames.join(', ');
        return `${groupNames.length} groups`;
      }
      return 'All groups';
    }
    const selectedTeams = value.map(id => teams.find(t => t._id === id)).filter(Boolean);
    if (selectedTeams.length === 0) return 'All groups';
    if (selectedTeams.length === 1) {
      const t = selectedTeams[0]!;
      const teamsInGroup = teams.filter(x => x.groupName === t.groupName);
      if (teamsInGroup.length <= 1) return t.groupName;
      return `${t.groupName} - ${t.name}`;
    }
    const groupNames = Array.from(new Set(selectedTeams.map(t => t!.groupName)));
    if (groupNames.length === 1) {
      const group = groupNames[0];
      const allTeamsInGroup = teams.filter(t => t.groupName === group);
      if (selectedTeams.length === allTeamsInGroup.length) return group;
      const officeNames = selectedTeams.map(t => t!.name);
      if (officeNames.length <= 2) return `${group} - ${officeNames.join(', ')}`;
      return `${group} (${officeNames.length} offices)`;
    }
    // Multiple groups - show group names only
    if (groupNames.length <= 2) {
      return groupNames.join(', ');
    }
    return `${groupNames.length} groups`;
  })();

  const selectStyle: React.CSSProperties = {
    fontSize: 13,
    padding: '0 30px 0 14px',
    borderRadius: 12,
    border: 'none',
    backgroundColor: 'white',
    height: 44,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    outline: 'none',
    transition: 'transform 0.15s, opacity 0.15s',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23999' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    whiteSpace: 'nowrap',
    fontWeight: 500,
  };

  return (
    <>
      {variant === 'chip' ? (
        <FilterChip
          label={label}
          color={FILTER_COLORS.group}
          onClick={() => setModalOpen(true)}
          onRemove={() => { setOpenClean(true); setModalOpen(true); }}
        />
      ) : (
        <div
          onClick={() => setModalOpen(true)}
          style={selectStyle}
          onMouseEnter={e => { e.currentTarget.style.transform = 'scale(0.97)'; e.currentTarget.style.opacity = '0.8'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.opacity = '1'; }}
        >
          {label}
        </div>
      )}
      <TeamModal
        visible={modalOpen}
        onClose={() => { setModalOpen(false); setOpenClean(false); onExternalClose?.(); }}
        onSave={(teams) => { setOpenClean(false); onChange(teams); }}
        teams={restrictToUserGroups ? availableTeams : teams}
        initialSelected={openClean ? [] : value}
        showAllOption={!restrictToUserGroups}
      />
    </>
  );
};

export default TeamSelect;
